import { useEffect, useMemo, useState } from 'react';
import type {
  CatalystPortalProxyResult,
  UpdateCatalystPortalSettingsInput,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { SelectField, TextField } from '../../components/ui/Field.js';
import { AccountNameSyncCard } from './AccountNameSyncCard.js';
import { TierSyncCard } from './TierSyncCard.js';
import { ContactSyncCard } from './ContactSyncCard.js';
import {
  useCatalystPortalProxy,
  useCatalystPortalSettings,
  useTestCatalystPortal,
  useUpdateCatalystPortalSettings,
} from './api.js';

interface EndpointOption {
  value: string;
  label: string;
}

/** Pulls the GET paths out of an OpenAPI document (handles a possible {data} envelope). */
function endpointsFromOpenApi(body: unknown): EndpointOption[] {
  const doc = (
    body && typeof body === 'object' && 'paths' in body
      ? body
      : body && typeof body === 'object' && 'data' in body
        ? (body as { data?: unknown }).data
        : null
  ) as { paths?: Record<string, Record<string, { summary?: string }>> } | null;
  const paths = doc?.paths;
  if (!paths) return [];
  const options: EndpointOption[] = [];
  for (const [path, methods] of Object.entries(paths)) {
    const get = methods?.get;
    if (!get) continue;
    const summary = typeof get.summary === 'string' ? get.summary : '';
    options.push({ value: path, label: summary ? `${path} — ${summary}` : path });
  }
  options.sort((a, b) => a.value.localeCompare(b.value));
  return options;
}

function formatBody(result: CatalystPortalProxyResult): string {
  if (result.raw != null) return result.raw;
  try {
    return JSON.stringify(result.body, null, 2);
  } catch {
    return String(result.body);
  }
}

export function CatalystPortalPage(): JSX.Element {
  const settingsQuery = useCatalystPortalSettings();
  const updateMutation = useUpdateCatalystPortalSettings();
  const testMutation = useTestCatalystPortal();
  const proxyMutation = useCatalystPortalProxy();
  const settings = settingsQuery.data;

  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [clearApiKey, setClearApiKey] = useState(false);
  const [saved, setSaved] = useState(false);

  const [path, setPath] = useState('/v1/health');
  const [endpoints, setEndpoints] = useState<EndpointOption[]>([]);

  useEffect(() => {
    if (!settings) return;
    setBaseUrl(settings.baseUrl ?? '');
    setApiKey('');
    setClearApiKey(false);
  }, [settings]);

  const handleSave = (): void => {
    setSaved(false);
    const input: UpdateCatalystPortalSettingsInput = { baseUrl: baseUrl.trim() || null };
    if (clearApiKey) {
      input.apiKey = null;
    } else if (apiKey.trim()) {
      input.apiKey = apiKey.trim();
    }
    updateMutation.mutate(input, {
      onSuccess: () => {
        setSaved(true);
        setApiKey('');
        setClearApiKey(false);
      },
    });
  };

  const handleLoadEndpoints = (): void => {
    proxyMutation.mutate('/v1/openapi.json', {
      onSuccess: (result) => setEndpoints(endpointsFromOpenApi(result.body)),
    });
  };

  const handleSend = (): void => {
    const trimmed = path.trim();
    if (!trimmed) return;
    proxyMutation.mutate(trimmed);
  };

  const saveError =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : updateMutation.error
        ? 'Failed to save Catalyst Portal settings.'
        : null;

  const proxyError =
    proxyMutation.error instanceof ApiError
      ? proxyMutation.error.message
      : proxyMutation.error
        ? 'The request failed.'
        : null;

  const testResult = testMutation.data;
  const proxyResult = proxyMutation.data;

  const endpointOptions = useMemo(
    () => [
      { value: '', label: endpoints.length ? 'Pick an endpoint…' : 'Load endpoints first' },
      ...endpoints,
    ],
    [endpoints],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Catalyst Portal"
        description="Connect to the Catalyst Public API (api.nscatalyst.com) and browse its read-only endpoints."
      />

      <Card>
        <DataState
          isLoading={settingsQuery.isLoading}
          isError={settingsQuery.isError}
          error={settingsQuery.error}
          onRetry={() => void settingsQuery.refetch()}
          isEmpty={false}
        >
          {settings ? (
            <div className="max-w-2xl space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={settings.configured ? 'green' : 'amber'}>
                  {settings.configured ? 'Configured' : 'No API key'}
                </Badge>
                {settings.managedInDatabase ? <Badge tone="blue">Managed in app</Badge> : null}
                {!settings.canStoreSecrets ? (
                  <span className="text-xs text-amber-600">
                    No encryption key on the server — the API key cannot be stored from the UI.
                  </span>
                ) : null}
              </div>

              <p className="text-sm text-slate-500">
                Send your Catalyst Public API key here. It is stored encrypted and sent as a Bearer
                token (and <code>X-Api-Key</code>) on every request. Find or create a key in the
                Catalyst licensing admin.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label="Base URL"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://api.nscatalyst.com"
                />
                <div>
                  <TextField
                    label="API key"
                    type="password"
                    value={apiKey}
                    onChange={(e) => {
                      setApiKey(e.target.value);
                      if (e.target.value) setClearApiKey(false);
                    }}
                    disabled={clearApiKey || !settings.canStoreSecrets}
                    placeholder={settings.hasApiKey ? '•••••••• (stored)' : 'Enter your API key'}
                  />
                  {settings.hasApiKey ? (
                    <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
                      <input
                        type="checkbox"
                        checked={clearApiKey}
                        onChange={(e) => {
                          setClearApiKey(e.target.checked);
                          if (e.target.checked) setApiKey('');
                        }}
                      />
                      Clear the stored API key
                    </label>
                  ) : null}
                </div>
              </div>

              {saveError ? <p className="text-sm text-rose-600">{saveError}</p> : null}
              {saved ? <p className="text-sm text-emerald-600">Settings saved.</p> : null}
              {testResult ? (
                <p className={`text-sm ${testResult.ok ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {testResult.message}
                </p>
              ) : null}

              <div className="flex items-center gap-2">
                <Button onClick={handleSave} isLoading={updateMutation.isPending}>
                  Save settings
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate()}
                  isLoading={testMutation.isPending}
                >
                  Test connection
                </Button>
              </div>
            </div>
          ) : null}
        </DataState>
      </Card>

      <AccountNameSyncCard />

      <TierSyncCard />

      <ContactSyncCard />

      <Card>
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Endpoint explorer</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Browse the API by sending read-only GET requests. Load the endpoint list, pick one,
              fill in any <code>{'{placeholders}'}</code>, and send.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[24rem] flex-1">
              <SelectField
                label="Endpoint"
                value=""
                onChange={(e) => {
                  if (e.target.value) setPath(e.target.value);
                }}
                options={endpointOptions}
              />
            </div>
            <Button
              variant="secondary"
              onClick={handleLoadEndpoints}
              isLoading={proxyMutation.isPending}
            >
              Load endpoints
            </Button>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[24rem] flex-1">
              <TextField
                label="Path"
                value={path}
                onChange={(e) => setPath(e.target.value)}
                placeholder="/v1/health"
              />
            </div>
            <Button
              onClick={handleSend}
              isLoading={proxyMutation.isPending}
              disabled={!path.trim()}
            >
              Send
            </Button>
          </div>

          {proxyError ? <p className="text-sm text-rose-600">{proxyError}</p> : null}

          {proxyResult ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Badge
                  tone={proxyResult.ok ? 'green' : 'red'}
                >{`HTTP ${proxyResult.status}`}</Badge>
                <span className="font-mono text-xs text-slate-500">GET {proxyResult.path}</span>
              </div>
              <pre className="max-h-[28rem] overflow-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-100">
                {formatBody(proxyResult)}
              </pre>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
